<template>
  <v-container fluid>
    <base-material-card color="primary" icon="mdi-map-plus" inline class="ml-0 px-5 py-4" title="Mapas de Conectividade">
      <v-btn
        v-if="accessReleased('MAPAS_CONECTIVIDADE_ADICIONAR')"
        color="success"
        dark
        elevation="1"
        class="mt-5"
        style="float: left"
        relative
        text
        medium
        @click="$router.push('add')"
      >
        <v-icon left size="30px">mdi-plus-circle</v-icon>
        Gerar Mapa
      </v-btn>
      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        class="break-search ml-auto mt-4"
        label="Procurar"
        hide-details
        single-line
        style="max-width: 250px"
      />
      <general-progress-bars class="mt-11" :items="downloadProgressData" prefixText="Baixando arquivo ..." />
      <v-divider class="mt-10" />
      <v-data-table
        :headers="headers"
        :items="items"
        :search.sync="search"
        :mobile-breakpoint="800"
        :loading="loading"
        :hide-default-header="loading"
        :hide-default-footer="loading"
        no-data-text="Nenhum mapa encontrado"
      >
        <template v-slot:[`item.data_registro`]="{ item }">{{ item.data_registro | formatToMonth }} v{{ item.versao }}</template>
        <template v-slot:[`item.status`]="{ item }">
          <general-status :status="item.status" />
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            :class="!['SUCESSO', 'FALHA'].includes(item.status) ? 'cursor-block' : ''"
            class="px-1"
            min-width="0"
            fab
            icon
            x-small
            title="Baixar Logs"
            :loading="downloadLogsTecnicos[item.id] !== undefined ? true : false"
            :disabled="!['SUCESSO', 'FALHA'].includes(item.status)"
            @click="() => baixarLogsTecnicos(item)"
          >
            <v-icon small>mdi-download</v-icon>
          </v-btn>
          <v-btn
            :class="!['SUCESSO'].includes(item.status) ? 'cursor-block' : ''"
            class="px-1"
            min-width="0"
            fab
            icon
            x-small
            title="Baixar Mapa"
            :loading="downloadProgressData[item.id] !== undefined ? true : false"
            :disabled="!['SUCESSO'].includes(item.status)"
            @click="() => baixarArquivoMapa(item)"
          >
            <v-icon small>mdi-map-search</v-icon>
          </v-btn>
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                id="action-more"
                color="white"
                class="ml-1"
                height="22px"
                width="22px"
                fab
                x-small
                elevation="1"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon small>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item v-if="canExecuteItem(item.status)" @click="executeItem(item.id)">
                <v-list-item-title>
                  <v-icon small>mdi-play</v-icon>
                  Executar
                </v-list-item-title>
              </v-list-item>
              <v-list-item v-if="canDeleteItem(item)" @click="openDialogDelete(item)">
                <v-list-item-icon class="mx-0 my-0 py-4">
                  <v-icon small>mdi-trash-can-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Deletar</v-list-item-title>
              </v-list-item>
              <v-list-item @click="openDialogCreationLog(item)">
                <v-list-item-title>
                  <v-icon small>mdi-table-search</v-icon>
                  Log
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
        <template v-slot:[`footer.page-text`]>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon color="primary" v-bind="attrs" v-on="on" @click="$emit('rechargeTable')">mdi-refresh</v-icon>
            </template>
            <span>Clique aqui para recarregar os mapas</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </base-material-card>
    <dialog-creation-log :dialog.sync="dialogCreationLog" :item="dialogCreationLogData" />
    <dialog-delete :dialog.sync="dialogDelete" @submitTriggered="deletarItem(dialogDeleteData.id)">
      <template slot="title">Deletar Mapa #{{ dialogDeleteData.id }}</template>
      <template slot="body">
        Tem certeza que deseja deletar o mapa
        <strong>#{{ dialogDeleteData.id }}</strong>
        ?
      </template>
    </dialog-delete>
  </v-container>
</template>

<script>
  import dayjs from 'dayjs';
  import MapasSequenciamentoEletricoService from '@/services/MapasSequenciamentoEletricoService';
  import DialogCreationLog from '@/components/general/DialogCreationLog';
  import DialogDelete from '@/components/general/DialogDelete';
  import GeneralStatus from '@/components/general/GeneralStatus';
  import GeneralProgressBars from '@/components/general/GeneralProgressBars';
  import powerupsActionsMixins from '@/mixins/powerupsActionsMixins';
  import profilePermissionsMixin from '@/mixins/profilePermissionsMixin';

  export default {
    components: {
      DialogCreationLog,
      DialogDelete,
      GeneralStatus,
      GeneralProgressBars
    },
    mixins: [powerupsActionsMixins, profilePermissionsMixin],
    props: {
      items: {
        type: Array,
        default: () => []
      },
      loading: {
        type: Boolean,
        required: true
      }
    },
    data: () => ({
      headers: [
        {
          text: '#',
          value: 'id'
        },
        {
          text: 'Título',
          value: 'nome'
        },
        {
          text: 'Mês dos dados',
          value: 'data_registro'
        },
        {
          text: 'CTMT',
          value: 'ctmt'
        },
        {
          text: 'Tipo do Mapa',
          value: 'tipo_mapa_txt'
        },
        {
          text: 'Status',
          value: 'status'
        },
        {
          sortable: false,
          text: 'Ações',
          value: 'actions',
          class: 'pl-4'
        }
      ],
      search: '',
      dialogDelete: false,
      dialogDeleteData: { id: null },
      downloadProgressData: {},
      downloadLogsTecnicos: {}
    }),
    methods: {
      canExecuteItem(status) {
        return status === 'SUCESSO' || status === 'FALHA';
      },
      executeItem(id) {
        MapasSequenciamentoEletricoService.executar(id)
          .then(() => {
            this.$toast.info('A criação do mapa ocorrerá em instantes. Por favor, aguarde.', '', {
              position: 'topRight',
              timeout: 2000
            });
            this.$emit('rechargeTable');
          })
          .catch(() => {
            this.$toast.error('Erro ao agendar a criação do mapa.', '', {
              position: 'topRight'
            });
          });
      },
      canDeleteItem(item) {
        return item.status === 'AGUARDANDO';
      },
      openDialogCreationLog(item) {
        this.dialogCreationLog = true;
        this.dialogCreationLogData = {
          username: item.usuario,
          created: item.created
        };
      },
      openDialogDelete(item) {
        this.dialogDelete = true;
        this.dialogDeleteData = {
          id: item.id
        };
      },
      deletarItem(id) {
        MapasSequenciamentoEletricoService.deletar(id)
          .then(() => {
            this.$toast.success('Mapa removido com sucesso.', '', {
              position: 'topRight'
            });
            this.$emit('rechargeTable');
          })
          .catch((error) => {
            this.$toast.error('Erro ao deletar o mapa.', '', {
              position: 'topRight'
            });
            console.error(error);
          });
        this.dialogDelete = false;
      },
      baixarArquivoMapa(mapa) {
        const { id, data_registro, versao, ctmt, tipo_mapa } = mapa;
        const timestamp = dayjs(new Date()).format('YYYYMMDDHHmm');
        let nomeArquivo = `mapa-ctmt-${ctmt.toLowerCase()}-tipo-${tipo_mapa}-`;
        nomeArquivo += `bdgd-mes-${data_registro}-versao-${versao}-${timestamp}.zip`;

        this.$set(this.downloadProgressData, id, { id });
        this.$toast.info('Preparando seu arquivo para download.', '', {
          position: 'topRight',
          timeout: 2000
        });

        MapasSequenciamentoEletricoService.baixarArquivoMapa(id, {
          timeout: 60 * 60 * 1000,
          onDownloadProgress: (progressEvent) => {
            let progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            let isComputable = progressEvent.lengthComputable;
            this.$set(this.downloadProgressData, id, {
              progress,
              isComputable,
              id
            });
          }
        })
          .then((res) => {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', nomeArquivo);
            document.body.appendChild(link);
            link.click();
          })
          .catch((error) => {
            this.$toast.error('Erro ao baixar o arquivo zip com o mapa.', '', {
              position: 'topRight'
            });
            console.error('Erro: ', error);
          })
          .finally(() => {
            this.$delete(this.downloadProgressData, id);
          });
      },
      baixarLogsTecnicos(mapa) {
        const { id } = mapa;
        this.$set(this.downloadLogsTecnicos, id, { id });
        MapasSequenciamentoEletricoService.baixarLogsTecnicos(id)
          .then((res) => {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `logs-tecnicos-mapas-sequenciamento-eletrico-${id}.log`);
            document.body.appendChild(link);
            link.click();
          })
          .catch(() => {
            this.$toast.error('Falha no download dos logs técnicos.', '', {
              position: 'topRight'
            });
          })
          .finally(() => this.$delete(this.downloadLogsTecnicos, id));
      }
    }
  };
</script>
